import { AddressCheck } from '@/components/address-check';
import { CompactButton, StandardButton } from '@/components/buttons';
import { ContentPanel } from '@/components/content-panel';
import { CurbyAccordion } from '@/components/curby-accordion';
import { CurbyHeader } from '@/components/curby-header';
import { CustomContentSection } from '@/components/custom-content-section';
import { useCurrentUser } from '@/hooks';
import { PrimaryLayout } from '@/layouts/primary';
import { Accordion, Flex, Text } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import {
	AccordionLocation,
	ContentSectionLocation,
	ContentSectionPlacement,
	MaterialType,
} from '../../../../backend/src/types.generated';
import { useGetUserLocationQuery } from './graphql.generated';
import styles from './styles.module.css';

export const BagScannedPage = () => {
	const navigate = useNavigate();
	const { material } = useSearch({ from: '/_app/bag-scanned' });
	const backToHome = useCallback(() => navigate({ to: '/' }), [navigate]);
	const backToScanBag = useCallback(() => navigate({ to: '/scan' }), [navigate]);
	const { currentUser } = useCurrentUser();

	const { data: userLocationData } = useGetUserLocationQuery({
		variables: { id: currentUser?.id ?? '' },
		skip: !currentUser?.id,
	});

	const closeButton = useMemo(
		() => (
			<CompactButton leftSection={<IconX />} onClick={backToHome}>
				Close
			</CompactButton>
		),
		[backToHome]
	);
	const footer = useMemo(
		() => (
			<Flex direction="column" w="100%" gap="md">
				<StandardButton onClick={backToScanBag}>Scan Another Bag</StandardButton>
				<StandardButton variant="light" onClick={backToHome}>
					Done
				</StandardButton>
			</Flex>
		),
		[backToScanBag, backToHome]
	);

	const address = userLocationData?.user;

	const nearestLocation = useMemo(() => {
		return (
			<Accordion.Item value={nearestLocationValue} className={styles.accordionItem}>
				<Accordion.Control className={styles.accordionControl}>
					<Text>Your nearest drop-off location:</Text>
				</Accordion.Control>
				<Accordion.Panel>
					{address &&
						Boolean(typeof address.lat === 'number' && typeof address.lng === 'number') && (
							<AddressCheck
								council={address.linkedCouncil?.name}
								postcode={address.postcode}
								state={address.state}
								lat={address.lat ?? 0}
								lng={address.lng ?? 0}
							/>
						)}
				</Accordion.Panel>
			</Accordion.Item>
		);
	}, [address]);

	const ScannedMessage = useMemo(() => {
		const prefix = 'Thank you for recycling your';
		const suffix = 'with Curby!';
		switch (material) {
			case MaterialType.BlendedFibre:
				return `${prefix} blended fibre ${suffix}`;
			case MaterialType.Cardboard:
				return `${prefix} cardboard ${suffix}`;
			case MaterialType.CoffeePods:
				return `${prefix} coffee pods ${suffix}`;
			case MaterialType.LiquidPaperBoard:
				return `${prefix} liquid paperboard ${suffix}`;
			case MaterialType.MixedPaper:
				return `${prefix} mixed paper ${suffix}`;
			case MaterialType.MixedPlastic:
				return `${prefix} mixed plastic ${suffix}`;
			case MaterialType.SoftPlastic:
				return `${prefix} soft plastic ${suffix}`;
			case MaterialType.Textiles:
				return `${prefix} textiles ${suffix}`;
			case MaterialType.GlassCoarse:
			case MaterialType.GlassMedium:
			case MaterialType.GlassFine:
			case MaterialType.GlassSuperfine:
			case MaterialType.GlassMoondust:
			case MaterialType.GlassSf_300_150:
			case MaterialType.GlassSf_500_300:
				return `${prefix} glass ${suffix}`;
			default:
				return `${prefix} ${material} ${suffix}`;
		}
	}, [material]);

	return (
		<PrimaryLayout
			header={{ title: 'Bag Scanned', children: closeButton }}
			footer={{ children: footer, py: 'xl' }}
		>
			<ContentPanel gap="md" align="flex-start">
				<CustomContentSection
					location={ContentSectionLocation.BagScanned}
					placement={ContentSectionPlacement.Top}
				/>
				<CurbyHeader flexProps={{ direction: 'row', align: 'flex-start', gap: 'sm', w: '100%' }}>
					<Flex direction="column" className={styles.entries}>
						<Text size="lg" fw={900}>
							{ScannedMessage}
						</Text>
					</Flex>
				</CurbyHeader>
				<CustomContentSection
					location={ContentSectionLocation.BagScanned}
					placement={ContentSectionPlacement.Middle}
				/>

				<CurbyAccordion
					title="Bag Drop Off"
					accordionLocation={AccordionLocation.BagScanned}
					additionalItems={nearestLocation}
					additionalItemsDefaultValue={nearestLocationValue}
					rootClassName={styles.accordion}
				/>
				<CustomContentSection
					location={ContentSectionLocation.BagScanned}
					placement={ContentSectionPlacement.Bottom}
				/>
			</ContentPanel>
		</PrimaryLayout>
	);
};

const nearestLocationValue = 'nearestLocation';
